import { create } from 'zustand'
import {
  DEFAULT_BEAUTY_SETTINGS,
  beautyBusinessFieldsEqual,
  beautySettingsApi,
  fromRemoteBeautySettings,
  patchBeautySettings,
  readLocalBeautySettings,
  writeLocalBeautySettings,
} from '@/lib/beautySettings'
import type { BeautySaveStatus, BeautySettings } from '@/types/beauty'

interface BeautySettingsState {
  initialized: boolean
  loadingRemote: boolean
  settings: BeautySettings
  lastSavedSettings: BeautySettings | null
  saveStatus: BeautySaveStatus
  remoteFetchStarted: boolean
  saveTimer: number | null
  saveInFlight: boolean
  init: () => void
  updateSettings: (patch: Partial<BeautySettings>) => void
  scheduleSave: () => void
  flushSave: () => Promise<void>
}

export const useBeautySettingsStore = create<BeautySettingsState>((set, get) => ({
  initialized: false,
  loadingRemote: false,
  settings: readLocalBeautySettings(),
  lastSavedSettings: null,
  saveStatus: 'idle',
  remoteFetchStarted: false,
  saveTimer: null,
  saveInFlight: false,
  init: () => {
    const local = readLocalBeautySettings()
    const initUpdatedAt = local.local_updated_at
    const localDirty = !local.synced_at && !beautyBusinessFieldsEqual(local, DEFAULT_BEAUTY_SETTINGS)
    set((state) => ({
      initialized: true,
      settings: local,
      lastSavedSettings: local.synced_at ? local : state.lastSavedSettings,
      saveStatus: local.synced_at ? 'saved' : 'dirty',
    }))
    if (get().remoteFetchStarted) {
      return
    }
    set({ remoteFetchStarted: true, loadingRemote: true })
    void beautySettingsApi.getBeautySettings().then((remote) => {
      const remoteSettings = fromRemoteBeautySettings(remote)
      const current = get().settings
      const currentDirty = (
        get().saveStatus === 'failed' ||
        (
          (!current.synced_at || get().saveStatus === 'dirty') &&
          !beautyBusinessFieldsEqual(current, DEFAULT_BEAUTY_SETTINGS)
        )
      )
      if (
        current.local_updated_at !== initUpdatedAt ||
        !beautyBusinessFieldsEqual(current, local) ||
        (currentDirty && !beautyBusinessFieldsEqual(current, remoteSettings))
      ) {
        writeLocalBeautySettings(current)
        set({
          settings: current,
          lastSavedSettings: remoteSettings,
          loadingRemote: false,
          saveStatus: 'dirty',
        })
        get().scheduleSave()
        return
      }
      if (localDirty && !beautyBusinessFieldsEqual(local, remoteSettings)) {
        writeLocalBeautySettings(local)
        set({
          settings: local,
          lastSavedSettings: remoteSettings,
          loadingRemote: false,
          saveStatus: 'dirty',
        })
        get().scheduleSave()
        return
      }
      writeLocalBeautySettings(remoteSettings)
      set({
        settings: remoteSettings,
        lastSavedSettings: remoteSettings,
        loadingRemote: false,
        saveStatus: 'saved',
      })
    }).catch(() => {
      set({ loadingRemote: false })
    })
  },
  updateSettings: (patch) => {
    const next = patchBeautySettings(get().settings, patch)
    if (beautyBusinessFieldsEqual(next, get().settings)) {
      return
    }
    writeLocalBeautySettings(next)
    set({ settings: next, saveStatus: 'dirty' })
    get().scheduleSave()
  },
  scheduleSave: () => {
    const existingTimer = get().saveTimer
    if (existingTimer !== null) {
      window.clearTimeout(existingTimer)
    }
    const timer = window.setTimeout(() => {
      set({ saveTimer: null })
      void get().flushSave()
    }, 800)
    set({ saveTimer: timer })
  },
  flushSave: async () => {
    const state = get()
    if (state.saveTimer !== null) {
      window.clearTimeout(state.saveTimer)
      set({ saveTimer: null })
    }
    const current = get().settings
    const lastSaved = get().lastSavedSettings
    if (lastSaved && beautyBusinessFieldsEqual(current, lastSaved)) {
      const saved = { ...current, synced_at: current.synced_at || Date.now() }
      writeLocalBeautySettings(saved)
      set({ settings: saved, saveStatus: 'saved' })
      return
    }
    if (get().saveInFlight) {
      set({ saveStatus: 'dirty' })
      return
    }
    set({ saveInFlight: true, saveStatus: 'saving' })
    const submitted = current
    try {
      const remote = await beautySettingsApi.updateBeautySettings(submitted)
      const saved = fromRemoteBeautySettings(remote)
      const latest = get().settings
      if (
        latest.local_updated_at !== submitted.local_updated_at ||
        !beautyBusinessFieldsEqual(latest, submitted)
      ) {
        writeLocalBeautySettings(latest)
        set({
          lastSavedSettings: saved,
          saveStatus: 'dirty',
          saveInFlight: false,
        })
        get().scheduleSave()
        return
      }
      writeLocalBeautySettings(saved)
      set({
        settings: saved,
        lastSavedSettings: saved,
        saveStatus: 'saved',
        saveInFlight: false,
      })
    } catch {
      const failed = { ...get().settings, synced_at: undefined }
      writeLocalBeautySettings(failed)
      set({ settings: failed, saveStatus: 'failed', saveInFlight: false })
    }
  },
}))
